import { useMemo, useCallback } from 'react';
import { formatDistance } from '../../utils/gps';

/**
 * Get cardinal direction from angle
 */
function getCardinalDirection(angle: number): string {
  const normalized = ((angle % 360) + 360) % 360;
  const directions = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  const index = Math.round(normalized / 45) % 8;
  return directions[index];
}

/**
 * Calculate visual intensity based on distance (0-1 scale)
 * Closer = higher intensity
 */
function getIntensity(distance: number): number {
  // Intensity ramps up from 0.3 at 500m+ to 1.0 at 25m
  const maxDistance = 500;
  const minDistance = 25;
  const clamped = Math.max(minDistance, Math.min(maxDistance, distance));
  const intensity = 1 - (clamped - minDistance) / (maxDistance - minDistance);
  return 0.3 + intensity * 0.7; // Range: 0.3 to 1.0
}

/**
 * Determine proximity zone for visual effects
 */
function getProximityZone(distance: number): 'far' | 'approaching' | 'close' {
  if (distance > 100) return 'far';
  if (distance > 25) return 'approaching';
  return 'close';
}

/** Other hunter data for compass display */
export interface OtherHunter {
  userId: string;
  displayName: string;
  bearing: number; // Bearing from current user to this hunter
  distance: number; // Distance in meters
  connectionStatus: 'connected' | 'weak' | 'disconnected';
}

interface CompassDisplayProps {
  /** Current device heading in degrees (0-360) */
  deviceHeading: number | null;
  /** Bearing to target in degrees (0-360) */
  targetBearing: number;
  /** Distance to target in meters */
  distance: number;
  /** Whether compass is calibrating/loading */
  isLoading?: boolean;
  /** Error message if compass unavailable */
  error?: string | null;
  /** Whether hunter is in close proximity (within 25m) */
  inProximity?: boolean;
  /** Hint photo URL to display when in proximity */
  hintPhotoUrl?: string | null;
  /** Other hunters to show on compass perimeter */
  otherHunters?: OtherHunter[];
}

/**
 * GPS-based static compass display for hunters
 * Shows direction to current pin target with north always at top
 * Arrow points directly to target using GPS bearing (no device compass needed)
 * When in proximity, shows hint image in center with yellow glow
 */
export function CompassDisplay({
  deviceHeading: _deviceHeading, // Kept for backwards compatibility, not used
  targetBearing,
  distance,
  isLoading = false,
  error: _error = null, // Kept for backwards compatibility, not used
  inProximity = false,
  hintPhotoUrl = null,
  otherHunters = [],
}: CompassDisplayProps) {
  // Arrow points directly to target bearing (GPS-based, north always at top)
  // The DirectionArrow SVG points upward (0deg = north), so we use bearing directly
  const arrowRotation = useMemo(() => {
    return targetBearing;
  }, [targetBearing]);

  // Format distance for display
  const formattedDistance = useMemo(() => formatDistance(distance), [distance]);

  // Visual intensity based on distance
  const intensity = useMemo(() => getIntensity(distance), [distance]);
  const proximityZone = useMemo(() => getProximityZone(distance), [distance]);

  // Color scheme: yellow when in proximity, red otherwise
  const glowColor = useMemo(() => inProximity 
    ? { r: 255, g: 200, b: 50 }   // Yellow/gold
    : { r: 255, g: 45, b: 85 },    // Red
  [inProximity]);

  // Helper to generate rgba string (memoized with color)
  const glowRgba = useCallback(
    (alpha: number) => `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, ${alpha})`,
    [glowColor]
  );

  // Dynamic glow styles
  const glowStyles = useMemo(() => {
    const glowIntensity = Math.round(intensity * 100);
    const pulseSpeed = inProximity ? '1.2s' : (proximityZone === 'approaching' ? '1.5s' : '2.5s');
    
    return {
      outerRing: {
        boxShadow: (proximityZone === 'approaching' || inProximity)
          ? `0 0 ${20 + glowIntensity * 0.4}px ${glowRgba(intensity * 0.6)}, 
             0 0 ${40 + glowIntensity * 0.6}px ${glowRgba(intensity * 0.3)},
             inset 0 0 ${10 + glowIntensity * 0.2}px ${glowRgba(intensity * 0.2)}`
          : `0 0 8px ${glowRgba(0.15)}`,
        borderColor: (proximityZone === 'approaching' || inProximity)
          ? glowRgba(0.4 + intensity * 0.6)
          : 'rgb(55, 65, 81)',
        animationDuration: pulseSpeed,
      },
      innerRing: {
        boxShadow: (proximityZone !== 'far' || inProximity)
          ? `0 0 ${15 * intensity}px ${glowRgba(intensity * 0.4)}`
          : 'none',
      },
      centerDot: {
        boxShadow: `0 0 ${8 + 12 * intensity}px ${glowRgba(0.5 + intensity * 0.5)}`,
        transform: `scale(${0.8 + intensity * 0.4})`,
      },
    };
  }, [intensity, proximityZone, inProximity, glowRgba]);

  // Suspenseful message based on distance
  const suspenseMessage = useMemo(() => {
    if (inProximity) return "It's nearby! Look around...";
    if (distance > 200) return 'The target awaits...';
    if (distance > 100) return 'Keep moving...';
    if (distance > 50) return "You're getting warmer...";
    if (distance > 25) return 'Getting close now...';
    return "Almost there!";
  }, [distance, inProximity]);

  // GPS-based compass doesn't need device orientation - always show if we have position

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="animate-spin">
          <CompassLoadingIcon />
        </div>
        <p className="text-gray-400 mt-4">Getting your location...</p>
      </div>
    );
  }

  return (
    <div 
      className="flex flex-col items-center justify-center flex-1"
      role="region"
      aria-label="Navigation compass"
    >
      {/* Inject dynamic keyframes */}
      <style>{`
        @keyframes compassPulse {
          0%, 100% { 
            transform: scale(1);
            filter: brightness(1);
          }
          50% { 
            transform: scale(1.02);
            filter: brightness(1.15);
          }
        }
        @keyframes arrowGlow {
          0%, 100% { filter: drop-shadow(0 0 ${8 * intensity}px ${glowRgba(intensity * 0.8)}); }
          50% { filter: drop-shadow(0 0 ${16 * intensity}px ${glowRgba(intensity)}); }
        }
        @keyframes centerPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.8; }
        }
        @keyframes hintPulse {
          0%, 100% { 
            box-shadow: 0 0 20px ${glowRgba(0.6)};
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 40px ${glowRgba(0.9)};
            transform: scale(1.02);
          }
        }
        @keyframes hintShake {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          10% { transform: translate(-1px, 0.5px) rotate(-0.3deg); }
          20% { transform: translate(0.5px, -0.5px) rotate(0.2deg); }
          30% { transform: translate(-0.5px, 1px) rotate(-0.2deg); }
          40% { transform: translate(1px, -0.5px) rotate(0.3deg); }
          50% { transform: translate(-0.5px, 0.5px) rotate(-0.1deg); }
          60% { transform: translate(0.5px, 0px) rotate(0.2deg); }
          70% { transform: translate(-1px, -0.5px) rotate(-0.2deg); }
          80% { transform: translate(0.5px, 0.5px) rotate(0.1deg); }
          90% { transform: translate(0px, -0.5px) rotate(-0.1deg); }
        }
        @keyframes hintBlur {
          0% { filter: blur(4px) saturate(1.2); }
          15% { filter: blur(6px) saturate(1.1); }
          25% { filter: blur(2px) saturate(1.3); }
          40% { filter: blur(5px) saturate(1.15); }
          55% { filter: blur(1px) saturate(1.4); }
          65% { filter: blur(3px) saturate(1.25); }
          80% { filter: blur(6px) saturate(1.1); }
          90% { filter: blur(2px) saturate(1.35); }
          100% { filter: blur(4px) saturate(1.2); }
        }
        @keyframes orbitParticle {
          0% { transform: rotate(0deg) translateX(160px) rotate(0deg); opacity: 0.8; }
          25% { opacity: 1; }
          50% { opacity: 0.6; }
          75% { opacity: 1; }
          100% { transform: rotate(360deg) translateX(160px) rotate(-360deg); opacity: 0.8; }
        }
        @keyframes orbitParticleReverse {
          0% { transform: rotate(360deg) translateX(170px) rotate(-360deg); opacity: 0.6; }
          50% { opacity: 1; }
          100% { transform: rotate(0deg) translateX(170px) rotate(0deg); opacity: 0.6; }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          20% { opacity: 1; transform: scale(1); }
          40% { opacity: 0.5; transform: scale(0.8); }
          60% { opacity: 1; transform: scale(1.2); }
          80% { opacity: 0.3; transform: scale(0.6); }
        }
        @keyframes energyPulse {
          0% { stroke-dashoffset: 0; opacity: 0.3; }
          50% { opacity: 0.8; }
          100% { stroke-dashoffset: 100; opacity: 0.3; }
        }
      `}</style>

      {/* Suspense message */}
      <div 
        className={`text-sm mb-4 italic transition-all duration-500 ${
          inProximity ? 'text-yellow-400' : (proximityZone === 'approaching' ? 'text-red-400' : 'text-gray-500')
        }`}
      >
        {suspenseMessage}
      </div>

      {/* Compass container with dynamic glow */}
      <div 
        className={`relative w-72 h-72 ${(proximityZone === 'approaching' || inProximity) ? 'animate-[compassPulse_1.5s_ease-in-out_infinite]' : ''}`}
        role="img"
        aria-label={inProximity ? `Target nearby at ${formattedDistance}` : `Compass pointing ${getCardinalDirection(arrowRotation)} towards target at ${formattedDistance}`}
      >
        {/* Outer compass ring with dynamic glow */}
        <div 
          className="absolute inset-0 rounded-full border-2 transition-all duration-300" 
          style={glowStyles.outerRing}
          aria-hidden="true" 
        />
        
        {/* Ambient glow rings (visible when approaching) */}
        {proximityZone === 'approaching' && !inProximity && (
          <>
            <div 
              className="absolute -inset-2 rounded-full border animate-ping border-red-500/20"
              style={{ animationDuration: '3s' }}
              aria-hidden="true"
            />
            <div 
              className="absolute -inset-4 rounded-full border animate-ping border-red-500/10"
              style={{ animationDuration: '4s', animationDelay: '0.5s' }}
              aria-hidden="true"
            />
          </>
        )}

        {/* Flair effects - only when in proximity */}
        {inProximity && (
          <>
            {/* Pulsing glow rings */}
            <div 
              className="absolute -inset-2 rounded-full border animate-ping border-yellow-500/30"
              style={{ animationDuration: '3s' }}
              aria-hidden="true"
            />
            <div 
              className="absolute -inset-4 rounded-full border animate-ping border-yellow-500/20"
              style={{ animationDuration: '4s', animationDelay: '0.5s' }}
              aria-hidden="true"
            />
            
            {/* Orbiting particles */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
              {/* Particle 1 */}
              <div 
                className="absolute w-2 h-2 rounded-full bg-yellow-400"
                style={{ 
                  animation: 'orbitParticle 6s linear infinite',
                  boxShadow: '0 0 8px rgba(250, 204, 21, 0.8)',
                }}
              />
              {/* Particle 2 */}
              <div 
                className="absolute w-1.5 h-1.5 rounded-full bg-yellow-300"
                style={{ 
                  animation: 'orbitParticle 8s linear infinite',
                  animationDelay: '-2s',
                  boxShadow: '0 0 6px rgba(253, 224, 71, 0.8)',
                }}
              />
              {/* Particle 3 - reverse orbit */}
              <div 
                className="absolute w-1 h-1 rounded-full bg-amber-300"
                style={{ 
                  animation: 'orbitParticleReverse 10s linear infinite',
                  boxShadow: '0 0 5px rgba(252, 211, 77, 0.8)',
                }}
              />
            </div>

            {/* Sparkle effects at compass points */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <div
                key={angle}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${angle}deg) translateY(-155px)`,
                  background: '#fcd34d',
                  boxShadow: '0 0 4px rgba(252, 211, 77, 0.9)',
                  animation: 'sparkle 2s ease-in-out infinite',
                  animationDelay: `${i * 0.25}s`,
                }}
                aria-hidden="true"
              />
            ))}

            {/* Energy arc SVG */}
            <svg 
              className="absolute -inset-6 w-[calc(100%+48px)] h-[calc(100%+48px)] pointer-events-none"
              aria-hidden="true"
            >
              <circle
                cx="50%"
                cy="50%"
                r="48%"
                fill="none"
                stroke="rgba(250, 204, 21, 0.4)"
                strokeWidth="2"
                strokeDasharray="10 20"
                style={{ animation: 'energyPulse 3s linear infinite' }}
              />
              <circle
                cx="50%"
                cy="50%"
                r="52%"
                fill="none"
                stroke="rgba(253, 224, 71, 0.3)"
                strokeWidth="1"
                strokeDasharray="5 15"
                style={{ animation: 'energyPulse 4s linear infinite reverse' }}
              />
            </svg>
          </>
        )}
        
        {/* Cardinal directions - hide when in proximity */}
        {!inProximity && (
          <CompassCardinals heading={0} intensity={intensity} inProximity={inProximity} />
        )}

        {/* Compass ring - expands to full size when in proximity */}
        <div 
          className={`absolute rounded-full border transition-all duration-500 overflow-hidden ${
            inProximity 
              ? 'inset-2 border-yellow-500/50' 
              : 'inset-8 border-gray-600 bg-gray-900/50'
          }`}
          style={{
            ...glowStyles.innerRing,
            animation: inProximity ? 'hintPulse 2s ease-in-out infinite' : 'none',
          }}
          aria-hidden="true"
        >
          {/* Hint photo in center when in proximity */}
          {inProximity && hintPhotoUrl && (
            <div className="w-full h-full overflow-hidden">
              <img
                src={hintPhotoUrl}
                alt="Hint"
                className="w-full h-full object-cover"
                style={{
                  animation: 'hintShake 8s ease-in-out infinite, hintBlur 12s ease-in-out infinite',
                }}
              />
            </div>
          )}
          {inProximity && !hintPhotoUrl && (
            <div className="w-full h-full flex items-center justify-center bg-yellow-900/30">
              <span className="text-4xl">🎯</span>
            </div>
          )}
        </div>

        {/* Direction arrow - only show when NOT in proximity */}
        {!inProximity && (
          <div
            className="absolute inset-12 flex items-center justify-center compass-arrow"
            style={{
              transform: `rotate(${arrowRotation}deg)`,
              animation: proximityZone !== 'far' ? `arrowGlow ${proximityZone === 'approaching' ? '1s' : '2s'} ease-in-out infinite` : 'none',
            }}
            aria-hidden="true"
          >
            <DirectionArrow intensity={intensity} />
            
            {/* Distance label on arrow (only >100m) */}
            {distance > 100 && (
              <div 
                className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
                           bg-gray-900/90 px-2 py-1 rounded text-sm font-mono text-red-400
                           border border-red-500/30"
              >
                {formattedDistance}
              </div>
            )}
          </div>
        )}

        {/* Center dot - only show when NOT in proximity */}
        {!inProximity && (
          <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <div 
              className="w-3 h-3 rounded-full bg-red-500 transition-all duration-300"
              style={{
                ...glowStyles.centerDot,
                animation: proximityZone === 'approaching' ? 'centerPulse 1s ease-in-out infinite' : 'none',
              }}
            />
          </div>
        )}

        {/* Other hunters on compass perimeter */}
        {otherHunters.length > 0 && (
          <HunterDots 
            hunters={otherHunters} 
            deviceHeading={0} 
          />
        )}
      </div>

      {/* Distance display - hide when in proximity */}
      {distance <= 100 && !inProximity && (
        <div className="mt-8 text-center" role="status" aria-live="polite" aria-atomic="true">
          <div 
            className={`text-5xl font-bold transition-all duration-300 ${
              proximityZone === 'approaching' ? 'text-red-400' : 'text-white'
            }`}
            style={{
              textShadow: proximityZone === 'approaching'
                ? `0 0 ${20 * intensity}px rgba(255, 45, 85, 0.6)` 
                : 'none',
            }}
            aria-label={`Distance: ${formattedDistance}`}
          >
            {formattedDistance}
          </div>
          <div className="mt-2 text-gray-400" aria-hidden="true">
            → Target
          </div>
        </div>
      )}

      {/* Proximity message - show when in proximity */}
      {inProximity && (
        <div className="mt-8 text-center">
          <div className="text-yellow-400/70 text-lg">🎯 Look around!</div>
        </div>
      )}

      {/* Far distance - show below compass */}
      {distance > 100 && (
        <div className="mt-8 text-center text-gray-500 text-sm">
          Navigate using the compass arrow
        </div>
      )}
    </div>
  );
}

/**
 * Cardinal direction markers - fixed positions with north at top
 * Enhanced with intensity-based styling
 */
function CompassCardinals({ heading: _heading, intensity, inProximity = false }: { heading: number; intensity: number; inProximity?: boolean }) {
  // Fixed cardinal positions (north at top)
  const cardinals = [
    { label: 'N', x: 144, y: 14 },   // Top center
    { label: 'E', x: 274, y: 144 },  // Right center
    { label: 'S', x: 144, y: 274 },  // Bottom center
    { label: 'W', x: 14, y: 144 },   // Left center
  ];

  return (
    <>
      {cardinals.map(({ label, x, y }) => {
        const isNorth = label === 'N';
        const northColor = inProximity ? 'text-yellow-400' : 'text-red-500';
        const northGlow = inProximity 
          ? `0 0 ${10 * intensity}px rgba(255, 200, 50, ${intensity * 0.8})`
          : `0 0 ${10 * intensity}px rgba(255, 45, 85, ${intensity * 0.8})`;

        return (
          <div
            key={label}
            className={`absolute text-sm font-bold ${
              isNorth ? northColor : 'text-gray-400'
            }`}
            style={{
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
              textShadow: isNorth ? northGlow : 'none',
            }}
          >
            {label}
          </div>
        );
      })}
    </>
  );
}

/**
 * Direction arrow SVG pointing upward with dynamic intensity
 */
function DirectionArrow({ intensity }: { intensity: number }) {
  // Calculate dynamic colors based on intensity
  const fillOpacity = 0.7 + intensity * 0.3;
  const highlightOpacity = 0.3 + intensity * 0.4;
  
  return (
    <svg
      width="80"
      height="120"
      viewBox="0 0 80 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-lg"
      style={{
        filter: `drop-shadow(0 0 ${6 + 10 * intensity}px rgba(255, 45, 85, ${intensity * 0.6}))`,
      }}
    >
      {/* Glow effect layer */}
      <path
        d="M40 0 L70 60 L50 50 L50 120 L30 120 L30 50 L10 60 Z"
        fill={`rgba(255, 45, 85, ${intensity * 0.3})`}
        style={{
          filter: `blur(${4 + 6 * intensity}px)`,
        }}
      />
      {/* Arrow pointing up */}
      <path
        d="M40 0 L70 60 L50 50 L50 120 L30 120 L30 50 L10 60 Z"
        fill="#FF2D55"
        fillOpacity={fillOpacity}
        stroke="#FF2D55"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Highlight */}
      <path
        d="M40 8 L58 52 L48 46 L48 112 L40 112 L40 8 Z"
        fill="#FF6B8A"
        opacity={highlightOpacity}
      />
      {/* Inner shine for extra intensity */}
      <path
        d="M40 4 L52 45 L44 42 L44 108 L40 108 L40 4 Z"
        fill="#FFB3C5"
        opacity={intensity * 0.3}
      />
    </svg>
  );
}

function CompassLoadingIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FF2D55"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" opacity="0.3" />
      <path d="M12 2 A10 10 0 0 1 22 12" />
    </svg>
  );
}

/**
 * Renders other hunters as dots on the compass perimeter with inward-facing labels
 * Uses absolute GPS bearings with north always at top
 */
function HunterDots({ hunters, deviceHeading: _deviceHeading }: { hunters: OtherHunter[]; deviceHeading: number }) {
  // Compass is 288px (w-72), radius to perimeter is ~155px (just outside outer ring)
  const radius = 155;
  const centerOffset = 144; // Half of 288px

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {hunters.map((hunter) => {
        // Calculate position on perimeter using absolute bearing (north = top)
        const angleRad = (hunter.bearing * Math.PI) / 180;
        // Use sin/cos positioned so 0 degrees (north) is at top
        const x = centerOffset + radius * Math.sin(angleRad);
        const y = centerOffset - radius * Math.cos(angleRad);
        
        // Color based on connection status
        const dotColor = hunter.connectionStatus === 'connected' 
          ? 'bg-blue-500' 
          : hunter.connectionStatus === 'weak' 
            ? 'bg-yellow-500' 
            : 'bg-gray-500';
        
        // Calculate rotation for label to point inward (toward center)
        const labelRotation = hunter.bearing + 180;
        
        // Determine if label should be flipped to stay readable
        const normalizedRotation = ((labelRotation % 360) + 360) % 360;
        const shouldFlip = normalizedRotation > 90 && normalizedRotation < 270;
        const finalRotation = shouldFlip ? labelRotation + 180 : labelRotation;
        
        return (
          <div
            key={hunter.userId}
            className="absolute flex flex-col items-center"
            style={{
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {/* Dot */}
            <div 
              className={`w-3 h-3 rounded-full ${dotColor} shadow-lg`}
              style={{
                boxShadow: `0 0 8px ${hunter.connectionStatus === 'connected' ? 'rgba(59, 130, 246, 0.6)' : 'rgba(156, 163, 175, 0.4)'}`,
              }}
            />
            {/* Name label - rotated to point inward */}
            <div
              className="absolute whitespace-nowrap text-xs font-medium text-blue-300"
              style={{
                transform: `rotate(${finalRotation}deg)`,
                transformOrigin: 'center center',
                top: shouldFlip ? -16 : 14,
              }}
            >
              {hunter.displayName.split(' ')[0]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CompassDisplay;
